import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, TriangleAlert } from "lucide-react";
import { Button } from "../../components/ui/button/index.js";
import { issueReasonI18nKey, issueTitleI18nKey } from "./campaigns-presentation.js";

type IssueSample = {
  reason: string;
  rowNumber?: number | null;
};

export function CampaignAudienceIssuesList({
  reasons,
  samples = [],
}: {
  reasons: ReadonlyArray<{ reason: string; count: number }>;
  samples?: readonly IssueSample[];
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const [openReason, setOpenReason] = useState<string | null>(null);

  if (reasons.length === 0) return null;

  return (
    <ul className="m-0 grid list-none divide-y divide-line/60 p-0" data-testid="campaign-audience-issues">
      {reasons.map((entry) => {
        const samplesForReason = samples.filter((sample) => sample.reason === entry.reason);
        const open = openReason === entry.reason;
        const conflict =
          entry.reason === "DUPLICATE_RECIPIENT_CONFLICT" || entry.reason === "DUPLICATE_RECIPIENT";
        return (
          <li
            key={entry.reason}
            className="grid gap-1 py-1.5"
          >
            <div className="flex items-center gap-2">
              {conflict ? (
                <Copy size={14} className="shrink-0 text-warning" aria-hidden="true" />
              ) : (
                <TriangleAlert size={14} className="shrink-0 text-warning" aria-hidden="true" />
              )}
              <p className="m-0 min-w-0 flex-1 text-[0.8125rem] font-medium">
                {t(issueTitleI18nKey(entry.reason), {
                  defaultValue: t(issueReasonI18nKey(entry.reason)),
                })}
              </p>
              <p className="m-0 shrink-0 text-[0.75rem] text-muted tabular-nums">
                {t("campaigns.issues.rowCount", { count: entry.count })}
              </p>
            </div>
            {samplesForReason.length > 0 ? (
              <div className="ps-6">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-1.5 text-[0.75rem]"
                  onClick={() => setOpenReason(open ? null : entry.reason)}
                >
                  {open
                    ? t("campaigns.issues.hideExamples")
                    : t("campaigns.issues.viewExamples")}
                </Button>
                {open ? (
                  <ul className="m-0 mt-1 grid list-none gap-0.5 p-0 text-[0.75rem] text-muted">
                    {samplesForReason.slice(0, 8).map((sample, index) => (
                      <li key={`${sample.reason}-${sample.rowNumber ?? index}`}>
                        {sample.rowNumber
                          ? t("campaigns.csv.issueRow", {
                              row: sample.rowNumber,
                              reason: t(issueReasonI18nKey(sample.reason)),
                            })
                          : t(issueReasonI18nKey(sample.reason))}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
